import { Coach, Player, request } from "./client";
import { registerMessages } from "./errors";

registerMessages({
  unauthorized: "Нужно войти заново",
  "admin access required": "Недостаточно прав",
  "name is required": "Не указано название чата",
  "failed to create chat": "Не удалось создать чат",
  "failed to list chats": "Не удалось загрузить чаты",
  "chat not found": "Чат не найден",
  "failed to delete chat": "Не удалось удалить чат",
  "failed to list members": "Не удалось загрузить участников чата",
  "failed to add member": "Не удалось добавить участников",
  "failed to remove member": "Не удалось убрать участника",
  "member missing user_id or user_type": "У участника не указан тип",
  "failed to list bans": "Не удалось загрузить блокировки",
  "failed to create ban": "Не удалось заблокировать участника",
  "failed to remove ban": "Не удалось снять блокировку",
  "invalid duration: use 10m, 1h, 1d, or forever": "Неизвестный срок блокировки",
  "chat_ids is required": "Не указаны чаты",
  "failed to get last messages": "Не удалось загрузить последние сообщения",
  "failed to list parents": "Не удалось загрузить родителей",
});

export type ChatUserType = "player" | "coach" | "parent" | "admin";

export interface Chat {
  id: string;
  name: string;
  avatar_url?: string | null;
  color: string;
  created_by: string;
  created_at: string;
}

export interface ChatMember {
  id: string;
  chat_id: string;
  user_id: string;
  user_type: ChatUserType;
  user_name: string;
  role: string;
  joined_at: string;
}

export interface ChatBan {
  id: string;
  chat_id: string;
  user_id: string;
  banned_by: string;
  reason?: string | null;
  expires_at?: string | null;
  created_at: string;
}

export interface NewChatMember {
  user_id: string;
  user_type: ChatUserType;
  user_name: string;
  role: "admin" | "member";
}

export type BanDuration = "10m" | "1h" | "1d" | "forever";

export interface LastMessage {
  id: string;
  chat_id: string;
  sender_name: string;
  content: string;
  created_at: string;
}

export interface ChatParent {
  id: string;
  name: string;
  is_active: boolean;
}

// The server caps one page of members at 200.
const MEMBERS_PAGE = 200;

const chatPath = (id: string) => `/api/chats/${encodeURIComponent(id)}`;

// The admin's own id is needed to put them into a chat they create, and the
// API has no "who am I" endpoint, so it is read from the JWT payload. The
// signature is not checked here: the server checks it on every request.
export function currentAdmin(): { id: string; name: string } | null {
  const payload = localStorage.getItem("admin_token")?.split(".")[1];
  if (!payload) return null;
  try {
    const binary = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const utf8 = Array.from(binary, (c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`).join("");
    const claims = JSON.parse(decodeURIComponent(utf8));
    if (claims?.role !== "admin" || typeof claims.actor_id !== "string" || !claims.actor_id) return null;
    return { id: claims.actor_id, name: typeof claims.name === "string" ? claims.name : "" };
  } catch {
    return null;
  }
}

export const chatsApi = {
  // Only chats the caller is a member of: the server has no "all chats" list.
  listChats: () => request<Chat[] | null>("/api/chats/"),

  countMembers: async (chatID: string) => {
    const page = await request<{ members: ChatMember[] | null; total: number }>(`${chatPath(chatID)}/members?limit=1`);
    return page.total;
  },

  listMembers: async (chatID: string) => {
    const all: ChatMember[] = [];
    for (;;) {
      const query = new URLSearchParams({ limit: String(MEMBERS_PAGE), offset: String(all.length) });
      const page = await request<{ members: ChatMember[] | null; total: number }>(`${chatPath(chatID)}/members?${query}`);
      const batch = page.members ?? [];
      all.push(...batch);
      if (batch.length === 0 || all.length >= page.total) return all;
    }
  },

  lastMessages: async (chatIDs: string[]): Promise<Record<string, LastMessage>> => {
    if (chatIDs.length === 0) return {};
    const query = new URLSearchParams({ chat_ids: chatIDs.join(",") });
    return (await request<Record<string, LastMessage> | null>(`/api/messages/last?${query}`)) ?? {};
  },

  createChat: (name: string, members: NewChatMember[]) =>
    request<Chat>("/api/chats/", { method: "POST", body: JSON.stringify({ name, members }) }),

  deleteChat: (chatID: string) => request<{ status: string }>(chatPath(chatID), { method: "DELETE" }),

  addMembers: (chatID: string, members: NewChatMember[]) =>
    request<{ status: string }>(`${chatPath(chatID)}/members`, { method: "POST", body: JSON.stringify({ members }) }),

  removeMember: (chatID: string, userID: string) =>
    request<{ status: string }>(`${chatPath(chatID)}/members/${encodeURIComponent(userID)}`, { method: "DELETE" }),

  listBans: (chatID: string) => request<ChatBan[] | null>(`${chatPath(chatID)}/bans`),

  ban: (chatID: string, userID: string, duration: BanDuration, reason: string | null) =>
    request<ChatBan>(`${chatPath(chatID)}/bans`, {
      method: "POST",
      body: JSON.stringify({ user_id: userID, duration, reason }),
    }),

  unban: (chatID: string, userID: string) =>
    request<{ status: string }>(`${chatPath(chatID)}/bans/${encodeURIComponent(userID)}`, { method: "DELETE" }),

  listPlayers: () => request<Player[] | null>("/api/players/"),
  listCoaches: () => request<Coach[] | null>("/api/coaches/"),
  listParents: () => request<ChatParent[] | null>("/api/parents/"),
};
